import { defaultCtxGetter } from '~/hooks/actions/utils/prepare-action-with-context';
import {
  authButtonsNamesArray,
  type AccountButtonGeneral,
  type AccountButtonStyles,
  type AuthButtons,
  type DropdownItem,
  type StaticLinks,
} from './types';

export type ProfileAvatarDisplayType = 'image' | 'first_initial' | 'initials';

export function getDropdownItems({
  staticLinks,
  styles,
  general,
  isLoggedIn,
}: {
  staticLinks: StaticLinks;
  styles: any;
  general: AccountButtonGeneral;
  isLoggedIn: boolean | null;
}): DropdownItem[] {
  const dropdownItems: DropdownItem[] = [];

  [1, 2, 3, 4].forEach((i: number) => {
    const label: string = styles[`extra_link_${i}_label`];
    const linkType: string = styles[`extra_link_${i}_type`];
    const href: string =
      linkType === 'external_link'
        ? styles[`extra_link_${i}_external_link`]
        : getStaticLinkPath(staticLinks, styles[`extra_link_${i}_target`]);
    const defaultImageSrc: string = styles[`extra_link_${i}_asset`];
    const defaultImageSwitch: boolean = styles[`extra_link_${i}_asset_switch`];
    const hoverImageSrc: string = styles[`extra_link_${i}_hover_asset`];
    const hoverImageSwitch: boolean =
      styles[`extra_link_${i}_asset_hover_switch`];

    dropdownItems.push({
      label,
      href,
      defaultImageSrc,
      defaultImageSwitch,
      hoverImageSrc,
      hoverImageSwitch,
    });
  });

  let isAuthButtonsDividerAdded: boolean = false;

  authButtonsNamesArray.forEach((authButtonType: AuthButtons) => {
    if (!styles[`show_${authButtonType}_on_dropdown`]) return;

    if (!isAuthButtonVisible({ authButtonType, isLoggedIn })) return;

    const isAuthButtonsDivider =
      !!styles.account_dropdown_menu_auth_buttons_styling_divider_switch;

    const authButton = getDropdownAuthButton(
      styles,
      general,
      authButtonType as AuthButtons,
      isAuthButtonsDivider && !isAuthButtonsDividerAdded,
      staticLinks
    );

    if (isAuthButtonsDivider) isAuthButtonsDividerAdded = true;

    if (authButton) dropdownItems.push(authButton);
  });

  return dropdownItems.filter(
    (item) =>
      (item?.label || (item?.defaultImageSwitch && item?.defaultImageSrc)) &&
      item?.href
  );
}

function getDropdownAuthButton(
  styles: AccountButtonStyles,
  general: AccountButtonGeneral,
  authButtonType: AuthButtons,
  isAuthButtonsDivider: boolean,
  staticLinks: StaticLinks
): DropdownItem | undefined {
  const label: string | undefined =
    styles[`account_dropdown_menu_${authButtonType}_default_title`];

  const defaultImageSrc: string | undefined =
    styles[`account_dropdown_menu_${authButtonType}_default_asset`];
  const defaultImageSwitch: boolean =
    !!styles[`account_dropdown_menu_${authButtonType}_default_asset`];

  const hoverImageSrc: string | undefined =
    styles[`account_dropdown_menu_${authButtonType}_hover_asset`];
  const hoverImageSwitch: boolean =
    !!styles[`account_dropdown_menu_${authButtonType}_hover_asset_switch`];

  const href: string = getAuthButtonHref({
    authButtonType,
    general,
    staticLinks,
  });

  return {
    label,
    href,
    defaultImageSrc,
    defaultImageSwitch,
    hoverImageSrc,
    hoverImageSwitch,
    isAuthButtonsDivider,
    authButtonType,
  };
}

function getAuthButtonHref({
  authButtonType,
  general,
  staticLinks,
}: {
  authButtonType: AuthButtons;
  general: AccountButtonGeneral;
  staticLinks: StaticLinks;
}): string {
  if (authButtonType === 'sign_out') return '/logout';
  if (authButtonType === 'sign_in') return '/login';

  if (authButtonType === 'sign_up') {
    if (general.sign_up_link_type === 'external_link')
      return general?.sign_up_link_path || '';

    return getStaticLinkPath(staticLinks, general.sign_up_link_target);
  }

  return '';
}

export function getStaticLinkPath(
  staticLinks: StaticLinks,
  target: string | undefined
): string {
  return (
    staticLinks.find((staticLink) => staticLink.screenId === target)?.link || ''
  );
}

/**
 * Whether the profile dropdown trigger replaces the legacy account icon.
 *
 * @param styles - Account button style configuration.
 * @returns `true` when profile dropdown visibility is enabled.
 */
export function isProfileDropdownVisible(styles: AccountButtonStyles): boolean {
  const visibility = styles.profile_dropdown_button_visibility ?? 'never';
  return visibility !== 'never';
}

/**
 * Returns the configured fallback asset for profile avatar display modes.
 *
 * @param styles - Account button style configuration.
 * @returns Fallback asset URL when configured.
 */
export function getProfileAvatarFallbackAsset(
  styles: AccountButtonStyles
): string | undefined {
  return (
    styles.profile_dropdown_avatar_fallback_asset ||
    styles.profile_dropdown_asset_default
  );
}

export function isAccountItemsVisibe({
  styles,
  isLoggedIn,
  manifestKey,
}: {
  styles: any;
  isLoggedIn: boolean | null;
  manifestKey: string;
}): boolean {
  const visibility: 'when_signed_in' | 'when_signed_out' | 'always' | 'never' =
    styles[manifestKey];

  if (visibility === 'never') return false;
  if (visibility === 'always') return true;
  if (isLoggedIn && visibility === 'when_signed_in') return true;
  if (!isLoggedIn && visibility === 'when_signed_out') return true;

  return false;
}

export function isAuthButtonVisible({
  authButtonType,
  isLoggedIn,
}: {
  authButtonType: AuthButtons;
  isLoggedIn: boolean | null;
}): boolean {
  if (authButtonType === 'sign_in' && isLoggedIn) return false;
  if (authButtonType === 'sign_up' && isLoggedIn) return false;
  if (authButtonType === 'sign_out' && !isLoggedIn) return false;
  return true;
}

/**
 * Returns the alternate storage key using colon/dot notation.
 *
 * @param key - Original storage key.
 * @returns Key with swapped namespace separator.
 */
export function getAlternateStorageKey(key: string): string {
  return key.includes('.') ? key.replace(/\./g, ':') : key.replace(/:/g, '.');
}

/**
 * Reads a value from session/local storage, supporting colon/dot key variants.
 * Values written by `sessionStorageSet` / `localStorageSet` are JSON-parsed.
 *
 * @param key - Configured storage key.
 * @returns Parsed string value or `null` when missing.
 */
export function getStorageValue(key: string | undefined): string | null {
  if (!key) return null;

  const raw: string | null =
    defaultCtxGetter(key) ?? defaultCtxGetter(getAlternateStorageKey(key));

  if (!raw) return null;

  try {
    const parsed: unknown = JSON.parse(raw);

    if (typeof parsed === 'string') return parsed;
    if (parsed === null || parsed === undefined) return null;

    return String(parsed);
  } catch {
    return raw;
  }
}

/**
 * Derives profile initials from a user name.
 *
 * @param userName - Full user or profile name.
 * @param displayType - Whether to return the first initial or two initials.
 * @returns Uppercase initials, or an empty string when the name is missing.
 */
export function getProfileInitials(
  userName: string | null,
  displayType: 'first_initial' | 'initials'
): string {
  if (!userName?.trim()) return '';

  const words: string[] = userName.trim().split(/\s+/).filter(Boolean);

  if (words.length === 0) return '';

  if (displayType === 'first_initial') {
    return words[0]![0]!.toUpperCase();
  }

  return words
    .slice(0, 2)
    .map((word) => word[0]!.toUpperCase())
    .join('');
}
